import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, Button, Table, Form } from 'react-bootstrap';
import useAuth from '../../pages/useAuth';
import AttendanceCard from '../../components/AttendanceCards';

const StatCard = ({ title, value, color = '#fff', active, onClick }) => (
     <Card onClick={onClick} style={{
          cursor: onClick ? 'pointer' : 'default',
          borderRadius: 8,
          border: active ? '2px solid #4f46e5' : '1px solid #eee',
          padding: 8,
          boxShadow: 'none',
          background: color // Use the color prop for the background
     }}>
          <Card.Body style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 8 }}>
               <div style={{ lineHeight: 1 }}>
                    <div style={{ fontSize: 18, color: '#666' }}>{title}</div>
               </div>
               <div style={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #b1b1b1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700
               }}>
                    {value ?? '—'}
               </div>
          </Card.Body>
     </Card>
);

const CompactPageButton = ({ children, active, onClick }) => (
     <Button size="sm" onClick={onClick}
          style={{
               minWidth: 25,
               height: 25,
               padding: '0 5px',
               borderRadius: 10,
               fontSize: 13,
               boxShadow: 'none',
               background: 'transparent',
               border: 'none',
               color: active ? '#4f46e5' : '#333',
               fontWeight: active ? 700 : 500
          }}>
          {children}
     </Button>
);

const HOD_Doc_Dashboard = () => {
     useAuth();
     const baseUrl = process.env.REACT_APP_BASE_URL || '';
     const [loading, setLoading] = useState(true);
     const [summary, setSummary] = useState({ total: 0, present: 0, on_leave: 0, absent: 0 });
     const [doctors, setDoctors] = useState([]);
     const [filter, setFilter] = useState('all'); // Default to 'all' for Total Doctors
     const [q, setQ] = useState('');
     const [page, setPage] = useState(1);
     const [pageSize, setPageSize] = useState(20);
     const [selectedDate, setSelectedDate] = useState(() => {
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
     });
     useEffect(() => {
          let mounted = true;
          const fetchData = async () => {
               setLoading(true);
               try {
                    let url = `${baseUrl}/doctorSummary`;
                    if (selectedDate) {
                         url += `?date=${encodeURIComponent(selectedDate)}`;
                    }
                    const res = await fetch(url, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                         },
                    });
                    const j = await res.json();
                    if (!mounted) return;
                    if (j && j.status && j.data) {
                         const s = j.data.summary || {};
                         setSummary({
                              total: s.total ?? 0,
                              present: s.present ?? 0,
                              on_leave: s.on_leave ?? 0,
                              absent: s.absent ?? 0,
                         });
                         setDoctors(Array.isArray(j.data.details) ? j.data.details : []);
                    } else {
                         setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                         setDoctors([]);
                    }
               } catch (err) {
                    console.error('HOD doctorSummary fetch error', err);
                    setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                    setDoctors([]);
               } finally {
                    if (mounted) setLoading(false);
               }
          };
          fetchData();
          return () => { mounted = false; };
     }, [baseUrl, selectedDate]);

     // filtered & searched list
     const filtered = useMemo(() => {
          const qlow = (q || '').trim().toLowerCase();
          return doctors.filter(d => {
               if (!d) return false;
               if (filter !== 'all' && (d.status || '').toLowerCase() !== filter) return false;
               if (!qlow) return true;
               return (
                    (d.employee_name || '').toLowerCase().includes(qlow) ||
                    (d.employee_code || '').toLowerCase().includes(qlow)
               );
          });
     }, [doctors, filter, q]);

     const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
     const pageClamped = Math.min(Math.max(1, page), totalPages);
     useEffect(() => { setPage(1); }, [filter, q, pageSize]);

     const pageItems = filtered.slice((pageClamped - 1) * pageSize, pageClamped * pageSize);

     // pagination window - max 10 pages
     const maxBtns = 10;
     let start = 1;
     let end = totalPages;
     if (totalPages > maxBtns) {
          start = Math.max(1, pageClamped - Math.floor(maxBtns / 2));
          end = start + maxBtns - 1;
          if (end > totalPages) {
               end = totalPages;
               start = Math.max(1, end - maxBtns + 1);
          }
     }

     return (
          <div style={{ padding: 12, minHeight: '100vh', fontSize: 14 }}>
               <div style={{ maxWidth: 1100, margin: '0 auto' }}>

                    <Row className="mb-2">
                         <Col xs={12}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                                   {/* Reordered as requested: total, present, absent, on_leave */}
                                   <StatCard
                                        title="Total Doctors"
                                        value={loading ? '…' : summary.total}
                                        color="#e8f0fe" // Pastel blue
                                        active={filter === 'all'}
                                        onClick={() => setFilter('all')}
                                   />
                                   <StatCard
                                        title="Doctors Present"
                                        value={loading ? '…' : summary.present}
                                        color="#e9f7ef" // Pastel green
                                        active={filter === 'present'}
                                        onClick={() => setFilter('present')}
                                   />
                                   <StatCard
                                        title="Doctors Absent"
                                        value={loading ? '…' : summary.absent}
                                        color="#fff7e6" // Pastel yellow
                                        active={filter === 'absent'}
                                        onClick={() => setFilter('absent')}
                                   />
                                   <StatCard
                                        title="Doctors On Leave"
                                        value={loading ? '…' : summary.on_leave}
                                        color="#fff1f0" // Pastel peach
                                        active={filter === 'on_leave'}
                                        onClick={() => setFilter('on_leave')}
                                   />
                              </div>
                         </Col>
                    </Row>

                    <Row className="mb-2" style={{ alignItems: 'center' }}>
                         <Col md={3}>
                              {/* Date picker to select date sent to GET request */}
                              <Form.Control
                                   type="date"
                                   value={selectedDate}
                                   onChange={e => setSelectedDate(e.target.value)}
                                   size="sm"
                              />
                         </Col>
                         <Col md={5}>
                              <Form.Control placeholder="Search by name or code" value={q} onChange={e => setQ(e.target.value)} size="sm" />
                         </Col>
                         <Col md={4} className="text-end">
                              <Form.Select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: 120, display: 'inline-block' }} size="sm">
                                   <option value={10}>10 / page</option>
                                   <option value={20}>20 / page</option>
                                   <option value={50}>50 / page</option>
                              </Form.Select>
                         </Col>
                    </Row>

                    <Card className="mb-2" style={{ borderRadius: 8 }}>
                         <Card.Body style={{ padding: 0 }}>
                              <Table hover responsive size="sm" className="mb-0" style={{ marginBottom: 0 }}>
                                   <thead>
                                        <tr style={{ fontSize: 13 }}>
                                             <th style={{ width: 40 }}>#</th>
                                             <th style={{ width: 140 }}>Code</th>
                                             <th>Name</th>
                                             <th>Punch In</th>
                                             <th>Punch Out</th>
                                             <th style={{ width: 110 }}>Status</th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {loading ? (
                                             <tr><td colSpan="6" className="text-center py-3">Loading…</td></tr>
                                        ) : pageItems.length === 0 ? (
                                             <tr><td colSpan="6" className="text-center py-3">No doctors found</td></tr>
                                        ) : (
                                             pageItems.map((d, idx) => (
                                                  <tr key={d.emp_id || d.employee_code || idx}>
                                                       <td style={{ verticalAlign: 'middle' }}>{(pageClamped - 1) * pageSize + idx + 1}</td>
                                                       <td style={{ verticalAlign: 'middle' }}>{d.employee_code}</td>
                                                       <td style={{ verticalAlign: 'middle' }}>{d.employee_name}</td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            {d.punch_in ? new Date(d.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                       </td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            {d.punch_in && d.punch_out && d.punch_in === d.punch_out && selectedDate === new Date().toISOString().split('T')[0]
                                                                 ? 'Now On Duty'
                                                                 : d.punch_out
                                                                      ? new Date(d.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                                      : '-'}
                                                       </td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            <span className={`badge ${d.status === 'present' ? 'bg-success' : d.status === 'absent' ? 'bg-danger' : 'bg-secondary'}`} style={{ textTransform: 'capitalize', fontSize: 12, padding: '4px 8px' }}>
                                                                 {d.status || '—'}
                                                            </span>
                                                       </td>
                                                  </tr>
                                             ))
                                        )}
                                   </tbody>
                              </Table>
                         </Card.Body>
                    </Card>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8 }}>
                         <div style={{ fontSize: 13, color: '#555' }}>
                              Showing {filtered.length === 0 ? 0 : (pageClamped - 1) * pageSize + 1} - {Math.min(pageClamped * pageSize, filtered.length)} of {filtered.length} doctors
                         </div>

                         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {/* pagination: show only numeric page buttons (max 10) */}
                              <div style={{ display: 'flex', gap: 6 }}>
                                   {Array.from({ length: end - start + 1 }).map((_, i) => {
                                        const p = start + i;
                                        return <CompactPageButton key={p} active={p === pageClamped} onClick={() => setPage(p)}>{p}</CompactPageButton>;
                                   })}
                              </div>
                         </div>
                    </div>

               </div>
          </div>
     );
};

export default HOD_Doc_Dashboard;